// @flow
/* eslint arrow-parens:0 */
/* eslint no-confusing-arrow:0 */

import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import pinReducer from 'app/common/redux/reducers/pin';
import type { Action } from 'app/common/redux/types';
import type { Payload } from 'app/common/redux/actions/resourceFetch';
import type { State as Pin } from 'app/common/redux/reducers/pin';

export type State = {
  [id: string]: Pin,
};

const DefaultPinRetrieval = (data) => data || [];

// If you add a new PIN_FEED_RESOURCES, please add an additional mapping to the trackingParamKey function below
// and a new case in the getMapKey function in webapp/app/packages/client-tracking-params/index.js in order to correctly
// log the tracking_params for the Pins in your new feed. If you have any questions, please reach out to juliac@
const PIN_FEED_RESOURCES = {
  AuthLandingPageResource: (data) => (data ? data.results : []),
  ArticleProductFeedResource: DefaultPinRetrieval,
  BaseSearchResource: (data) => (data ? data.results : []),
  BoardContentRecommendationResource: DefaultPinRetrieval,
  BoardFeedResource: DefaultPinRetrieval,
  BoardSectionPinsResource: DefaultPinRetrieval,
  BusinessProfileUserPinsResource: DefaultPinRetrieval,
  CategoryFeedResource: DefaultPinRetrieval,
  ConversationRelatedPinsResource: DefaultPinRetrieval,
  BoardIdeasCardFeedResource: DefaultPinRetrieval,
  DomainFeedResource: DefaultPinRetrieval,
  DynamicInsertionPinResource: (data) => (data ? data.objects : []),
  ExploreTabStoryResource: (data) =>
    data ? data.objects.reduce((pins, item) => pins.concat(item.objects || []), []) : [],
  ExploreTabBlockPinResource: DefaultPinRetrieval,
  FollowingFeedResource: DefaultPinRetrieval,
  InterestResource: (data) => (data ? data.results : []),
  MerchantFeedResource: DefaultPinRetrieval,
  NativeUserPinsResource: DefaultPinRetrieval,
  NewsHubDetailsResource: (data) =>
    data
      ? data.reduce(
          (pins, item) =>
            item.content_items
              ? pins.concat(item.content_items.map((ci) => ci.content_object).filter(Boolean))
              : pins,
          [],
        )
      : [],
  ProfileCoverFeedResource: DefaultPinRetrieval,
  OverviewUserDiscoveredPinsResource: DefaultPinRetrieval,
  OverviewUserPinsResource: DefaultPinRetrieval,
  RelatedPinFeedResource: DefaultPinRetrieval,
  RelatedPinsFeedFromPinLandingResource: DefaultPinRetrieval,
  RelatedProductFeedResource: DefaultPinRetrieval,
  SearchResource: DefaultPinRetrieval,
  StoreFrontFeedResource: DefaultPinRetrieval,
  TopicFeedResource: DefaultPinRetrieval,
  UserDiscoveredPinsResource: DefaultPinRetrieval,
  UserMostRepinnedPinResource: DefaultPinRetrieval,
  UserHomefeedResource: DefaultPinRetrieval,
  UserPinsResource: DefaultPinRetrieval,
  VisualLinkRelatedProductFeedResource: DefaultPinRetrieval,
  VisualLiveSearchResultResource: DefaultPinRetrieval,
  VisualLinkVisualSearchResource: DefaultPinRetrieval,
};

const trackingParamKey = (payload: Payload) => {
  const resource = payload.name;
  let resourceKeyName = resource;
  let id;
  switch (resource) {
    case 'BaseSearchResource':
    case 'SearchResource':
      const query = payload.options.query;
      const landingPageType = payload.options.auth_lp_type;
      if (landingPageType === 'klp' && query) {
        const q = query.toLowerCase();
        id = q.split(' ').join('-');
      } else if (landingPageType === 'blp') {
        // For board landing pages, we don't have access to the query in the url so we cannot key on this value
        id = null;
      } else if (query) {
        id = encodeURIComponent(query);
      }
      resourceKeyName = 'SearchResource';
      break;
    case 'CategoryFeedResource':
      id = payload.options.feed;
      break;
    case 'DomainFeedResource':
      id = payload.options.domain;
      break;
    case 'ExploreTabStoryResource':
      id = payload.options.story_id;
      break;
    case 'NewsHubDetailsResource':
      id = payload.options.news_id;
      break;
    case 'RelatedPinFeedResource':
      id = payload.options.pin;
      break;
    case 'RelatedProductFeedResource':
      id = payload.options.pin;
      break;
    case 'ArticleProductFeedResource':
      id = payload.options.article;
      break;
    case 'TopicFeedResource':
      id = payload.options.interest;
      break;
    default:
  }
  if (id) {
    return `${resourceKeyName}_${id.toLowerCase()}`;
  }
  return resource;
};

// NOTE(yansun): keep the same reference if the new data doesn't differ from existing data this is redux convention,
// source: https://redux.js.org/faq/performance#do-i-have-to-deep-clone-my-state-in-a-reducer-isn-t-copying-my-state-going-to-be-slow
const mergePin = (prevPin, nextPin) => {
  const mergedPin = merge({}, prevPin, nextPin);
  return isEqual(prevPin, mergedPin) ? prevPin : mergedPin;
};

const pinsReducer = (state: State = {}, action: Action): State => {
  if (action.type === 'RESOURCE_FETCH_COMPLETE' || action.type === 'RESOURCE_FETCH_MORE_COMPLETE') {
    const { payload } = action;
    if (payload.name in PIN_FEED_RESOURCES) {
      // $FlowIssue it doesn't get that PIN_FEED_RESOURCES has payload.name now
      const pins = PIN_FEED_RESOURCES[payload.name](payload.data) || [];
      const nextState = { ...state };
      pins.forEach((pin) => {
        if (pin.type === 'pin') {
          const key = trackingParamKey(payload);
          let tracking_params_map = {};

          // If the pin is already in redux, we want to start with the existing tracking_params_map
          if (state[pin.id]) {
            tracking_params_map = state[pin.id].tracking_params_map || {};
          }
          tracking_params_map[key] = pin.tracking_params;
          pin.tracking_params_map = tracking_params_map;
        }
        // activity data is already normalized by the activityItems reducer, no need to store duplicate data
        if (pin.board_activity) {
          pin.board_activity = { id: pin.board_activity.id };
        }
        // Some resources such as SearchResource can return multiple types of items depending on their options
        if (!pin.type || pin.type === 'pin') {
          nextState[pin.id] = mergePin(state[pin.id], pin);
        }
      });
      return nextState;
    } else if (
      (payload.name === 'PinResource' ||
        payload.name === 'PinPageResource' ||
        payload.name === 'StoryPinEditResource') &&
      payload.data
    ) {
      const {
        data: pin,
        options: { preserve_component_state: shouldPreserveComponentState = false },
      } = payload;
      const key = trackingParamKey(payload);

      let tracking_params_map = {};
      // If the pin is already in redux, we want to start with the existing tracking_params_map
      if (state[pin.id]) {
        tracking_params_map = state[pin.id].tracking_params_map || {};
      }
      tracking_params_map[key] = pin.tracking_params;
      pin.tracking_params_map = tracking_params_map;
      if (pin.board_activity) {
        pin.board_activity = { id: pin.board_activity.id };
      }
      return {
        ...state,
        [pin.id]: shouldPreserveComponentState
          ? mergePin(pin, state[pin.id])
          : mergePin(state[pin.id], pin),
      };
    } else if (payload.name === 'BoardActivityResource' && payload.data && payload.data.pin) {
      const { data: boardActivityItem } = payload;
      const {
        id: activityId,
        pin: { id: pinId },
      } = boardActivityItem;
      if (state[pinId] && !state[pinId].board_activity) {
        return {
          ...state,
          [pinId]: mergePin(state[pinId], { board_activity: { id: activityId } }),
        };
      }
    }
  } else if (action.type === 'ACTIVITY_ITEM_UPDATED') {
    const {
      payload: { data: activityItem },
    } = action;
    const pinId = (activityItem && activityItem.pin && activityItem.pin.id) || null;
    if (pinId && state[pinId] && !state[pinId].board_activity) {
      // activity data is already normalized by the activityItems reducer, no need to store duplicate data
      return {
        ...state,
        [pinId]: mergePin(state[pinId], { board_activity: { id: activityItem.id } }),
      };
    }
  }

  // Although we have a single pin reducer that mutates pin data, that data lives
  // in each of this state's keys. This means that we need to pass each action to
  // each pin in each key to see if it has been mutated. If no pin is mutated, we
  // don't need to mutate this state which should save us some redux emits.
  const nextState = { ...state };
  let mutatedPins = false;
  Object.keys(nextState).forEach((id) => {
    const pin = nextState[id];
    nextState[id] = pinReducer(pin, action);

    if (nextState[id] !== state[id]) {
      mutatedPins = true;
    }
  });

  return mutatedPins ? nextState : state;
};

export default pinsReducer;
